import * as Yup from 'yup';

import { FIELD_IS_REQUIRED } from 'src/common/validation/errors';
import { CANADA_POSTAL_CODE, ZIP_EXT_LENGTH } from 'src/common/validation/validators';
import { Country } from 'src/types';
import type { IAddress } from 'src/types/IAddress';

export type IAddressFormValues = IAddress;

const postalCodevalidationSchema = (schema: Yup.StringSchema, countryFieldName: string) =>
  schema.when(countryFieldName, {
    is: (country: string) => country === Country.Canada,
    then: (subSchema) => subSchema.matches(...CANADA_POSTAL_CODE),
    otherwise: (subSchema) => subSchema.length(...ZIP_EXT_LENGTH),
  });

export function getPostalCodeValidator(countryFieldName: string, isRequired: boolean) {
  return Yup.string()
    .when({
      is: () => isRequired,
      then: (schema) => schema.required(FIELD_IS_REQUIRED),
      otherwise: (schema) => schema.notRequired(),
    })
    .when({
      is: (postalCode?: string | null) => !!postalCode,
      then: (schema) => postalCodevalidationSchema(schema, countryFieldName),
    })
    .default(isRequired ? '' : null);
}

export const requiredAddressValidationSchema = Yup.object<IAddressFormValues>({
  country: Yup.string().required(FIELD_IS_REQUIRED).default(Country.Usa),
  city: Yup.string().required(FIELD_IS_REQUIRED).default(''),
  street: Yup.string().required(FIELD_IS_REQUIRED).default(''),
  region: Yup.string().required(FIELD_IS_REQUIRED).default(''),

  postalCode: getPostalCodeValidator('country', true),
});

export const addressValidationSchema = Yup.object<IAddressFormValues>({
  country: Yup.string().nullable().default(null),
  street: Yup.string().nullable().default(null),
  city: Yup.string()
    .when('country', {
      is: (country: string) => country != null,
      then: (schema) => schema.required(FIELD_IS_REQUIRED),
      otherwise: (schema) => schema.nullable(),
    })
    .default(null),
  region: Yup.string().nullable().default(null),
  postalCode: getPostalCodeValidator('country', false).nullable(),
});

export function toAddressInitialValues(address: IAddress): IAddressFormValues {
  return {
    country: address.country,
    street: address.street,
    city: address.city,
    region: address.region,
    postalCode: address.postalCode,
  };
}
